import { Fragment } from 'react';
import {
	Box,
	Button,
	CardContent,
	Divider,
	Stack,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import { useQuoteContext } from '../../shared/contexts/QuoteContext';
import DocumentDownload from '../DocumentDownload';
import DownloadAllDocuments from '../DownloadAllDocuments';
import { colours } from '../../shared/colors';
import { useGetPolicyWordingsByPolicyIdListQuery } from '../../services/features/documentsApi/documentsApi';
import fileDownload from 'js-file-download';
import axios from 'axios';
import { NonApplicablePolicyDocuments } from '../../helper/nonApplicableDocuments';
import DownloadIcon from '@mui/icons-material/Download';
import { DocumentType } from  '../../helper/enum/DocumentType';

/**
 * Component for rendering policy-bound information.
 * @param {Object} props - Props for the component.
 * @returns {JSX.Element} - The JSX element representing the policy information.
 */
const PolicyBoundView = () => {
	const { currentQuotes, policyIds, isQuoteDisabled } = useQuoteContext();

	const { data: policyUrls } = useGetPolicyWordingsByPolicyIdListQuery(policyIds, {
		skip: policyIds.length === 0,
	});

	/**
	 * Generate rows for policy documents table.
	 * @returns {JSX.Element} - The JSX element representing the policy document rows.
	 */
	const generateDocumentRows = () => {
		return (
			<TableContainer>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell
								colSpan={2}
								sx={{
									fontWeight: 'bold',
								}}
							>
								PRODUCT
							</TableCell>
							<TableCell align='right'>
								<DownloadAllDocuments types={'all'} />
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{currentQuotes?.map((quote) => {
							let productName = '';
							if ((quote.quoteProducts ?? []).length > 0) {
								productName = quote.quoteProducts[0].name;
							} else if ((quote.quotePackages ?? []).length) {
								productName = quote.quotePackages[0].name;
							}
							const disabled = isQuoteDisabled(quote.id);
							const wordings = (policyUrls?.[quote.policyId] ?? []).filter(
								(w) => w?.policyWordingUrl.length > 0
							);

							console.log('policy wording', {
								wordings,
								policyId: quote.policyId,
								policyUrls,
							});

							return (
								<Fragment key={`table-rows-quote-${quote.id}`}>
									<TableRow hover key={`quote-${quote.id}`}>
										<TableCell colSpan={3}>{productName}</TableCell>
									</TableRow>
									<TableRow hover key={`record-of-answers-${quote.id}`}>
										<TableCell component='th' scope='row' padding='checkbox' />
										<TableCell component='th' scope='row' padding='none'>
											Record of Answers (PDF)
										</TableCell>
										<TableCell align='right'>
											<DocumentDownload
												quoteDataId={quote.id}
												type={DocumentType.DecisionTree}
											/>
										</TableCell>
									</TableRow>
									<TableRow hover key={`quote-document-${quote.id}`}>
										<TableCell component='th' scope='row' padding='checkbox' />
										<TableCell component='th' scope='row' padding='none'>
											Quote Document (PDF)
										</TableCell>
										<TableCell align='right'>
											<DocumentDownload 
												quoteDataId={quote.id} 
												type={DocumentType.Quote}
											/>
										</TableCell>
									</TableRow>
									<TableRow hover key={`policy-schedule-${quote.id}`}>
										<TableCell component='th' scope='row' padding='checkbox' />
										<TableCell component='th' scope='row' padding='none'>
											Policy Schedule (PDF)
										</TableCell>
										<TableCell align='right'>
											<DocumentDownload
												quoteDataId={quote.id}
												type={DocumentType.Policy}
											/>
										</TableCell>
									</TableRow>
									{!(quote.nonApplicablePolicyDocuments ?? []).includes(
										NonApplicablePolicyDocuments.CertificateOfCurrency
									) && (
										<TableRow hover key={`insurance-certificate-${quote.id}`}>
											<TableCell
												component='th'
												scope='row'
												padding='checkbox'
											/>
											<TableCell component='th' scope='row' padding='none'>
												Certificate of Currency (PDF)
											</TableCell>
											<TableCell align='right'>
												<DocumentDownload
													quoteDataId={quote.id}
													type={DocumentType.Certificate}
												/>
											</TableCell>
										</TableRow>
									)}

									{wordings?.map((wording, index) => (
										<TableRow
											hover
											key={`policy-wording-policy-${wording.policyWordingName}-${index}`}
										>
											<TableCell
												component='th'
												scope='row'
												padding='checkbox'
											/>
											<TableCell component='th' scope='row' padding='none'>
												<span>
													<span
														style={{ paddingRight: '0.25em' }}
													>{`${wording.productName}`}</span>
													<span>{'Policy wording (PDF)'}</span>
												</span>
											</TableCell>
											<TableCell align='right'>
												<DocumentDownload 
													quoteDataId={quote.id}
													type={DocumentType.Wording}
													wording={wording}
												/>
											</TableCell>
										</TableRow>
									))}
								</Fragment>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
		);
	};

	return (
		<div className='policy_container'>
			<Stack direction='row' px={'1.5em'} py={'1em'} alignItems='center'>
				<Box pr={'0.5em'} alignItems='center'>
					<CheckCircleRoundedIcon
						sx={{ fontSize: '2em', color: colours.primaryDark.main }}
					/>
				</Box>
				<Typography variant='quoteTabHeader'>{'Policy Bound'}</Typography>
			</Stack>
			<Divider light />
			<CardContent sx={{ px: '1.5em', py: '1em' }}>
				<Stack spacing={1} pb='2em'>
					<Typography variant='cardContentHeader'>
						Congratulations on confirming your cover!
					</Typography>
					<Typography variant='subtitle1'>
						Please note you are not required to send a Closing to Delta to confirm this
						policy placement.
					</Typography>
				</Stack>
				{generateDocumentRows()}
			</CardContent>
		</div>
	);
};

export default PolicyBoundView;
